import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  BackendResponse,
  BatchCopyWorkflowsRequest,
  BatchCopyWorkflowsResponse,
  EmailDynamicContent,
  EmailTemplate,
  RoleParticipantsData,
  TriggerPayload,
  WorkflowPayload,
  WorkflowTaskPayload,
} from '@qtek/shared/models';
import { Observable } from 'rxjs';

export type WorkflowEntity =
  | 'Workflow'
  | 'WorkflowTaskConfiguration'
  | 'Trigger';

@Injectable({ providedIn: 'root' })
export class WorkflowApiService {
  private readonly http = inject(HttpClient);
  private readonly ws = inject(WebSocketService);
  private readonly managers: Record<WorkflowEntity, WSEntityManager> = {
    Workflow: new WSEntityManager(this.ws, 'Workflow'),
    WorkflowTaskConfiguration: new WSEntityManager(
      this.ws,
      'WorkflowTaskConfiguration'
    ),
    Trigger: new WSEntityManager(this.ws, 'Trigger'),
  };
  private readonly options = { withCredentials: true };

  subscribe(
    ent: WorkflowEntity,
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
    filter?: (message: BaseWebsocketMessage) => boolean
  ): Observable<BaseWebsocketMessage> {
    return this.managers[ent].subscribeEntity(payload, undefined, filter);
  }

  queryWorkflows(itemId: string, mysid: string): void {
    this.managers.Workflow.query({ mysid, view: 'plain', prms: { itemId } });
  }
  queryMyDeals(mysid: string): void {
    this.managers.Workflow.query({ mysid, view: 'myDeals' });
  }
  getDetails(id: string, mysid: string): void {
    this.managers.Workflow.get({ id, mysid, view: 'details' });
  }

  createWorkflow(res: WorkflowPayload, mysid: string): void {
    this.managers.Workflow.create({ res, mysid });
  }
  updateWorkflow(
    id: string,
    res: Partial<WorkflowPayload>,
    mysid: string
  ): void {
    this.managers.Workflow.update({ id, res, mysid });
  }
  deleteWorkflow(id: string, mysid: string): void {
    this.managers.Workflow.delete({ id, mysid });
  }

  createTask(res: WorkflowTaskPayload, mysid: string): void {
    this.managers.WorkflowTaskConfiguration.create({ res, mysid });
  }
  updateTask(
    id: string,
    res: Partial<WorkflowTaskPayload>,
    mysid: string
  ): void {
    this.managers.WorkflowTaskConfiguration.update({ id, res, mysid });
  }
  swapTasks(a: string, b: string, mysid: string): void {
    this.managers.WorkflowTaskConfiguration.bulkUpdate({
      res: [{ _id: a }, { _id: b }],
      mysid,
    });
  }
  deleteTask(id: string, mysid: string): void {
    this.managers.WorkflowTaskConfiguration.delete({ id, mysid });
  }

  createTrigger(res: TriggerPayload, mysid: string): void {
    this.managers.Trigger.create({ res, mysid });
  }
  updateTrigger(id: string, res: Partial<TriggerPayload>, mysid: string): void {
    this.managers.Trigger.update({ id, res, mysid });
  }
  deleteTrigger(id: string, mysid: string): void {
    this.managers.Trigger.delete({ id, mysid });
  }

  copyWorkflows(
    request: BatchCopyWorkflowsRequest
  ): Observable<BackendResponse<BatchCopyWorkflowsResponse>> {
    return this.http.post<BackendResponse<BatchCopyWorkflowsResponse>>(
      '/api/v2/workflow/batch/copy',
      request,
      this.options
    );
  }
  getRoleParticipants(
    itemId: string,
    roles: string[]
  ): Observable<BackendResponse<RoleParticipantsData>> {
    return this.http.get<BackendResponse<RoleParticipantsData>>(
      '/api/v2/deals/user-with-role-in-deal',
      {
        ...this.options,
        params: { itemId, selectedRoles: roles.join(',') },
      }
    );
  }
  getEmailDynamicContent(
    taskId?: string
  ): Observable<BackendResponse<EmailDynamicContent>> {
    return this.http.get<BackendResponse<EmailDynamicContent>>(
      '/api/v2/email-dynamic-content',
      {
        ...this.options,
        params: taskId ? { taskId } : {},
      }
    );
  }
  getEmailTemplates(): Observable<BackendResponse<EmailTemplate[]>> {
    return this.http.get<BackendResponse<EmailTemplate[]>>(
      '/api/v2/email-templates',
      this.options
    );
  }
}
